/**
 * Трансляция или камера в отдельном окне поверх всех приложений.
 *
 * В браузере — Document Picture-in-Picture: окно всегда поверх, и в нём наш собственный <video>.
 * В приложении для компьютера (Electron 38) documentPictureInPicture тоже есть, но окна не
 * получается: открытие уходит в setWindowOpenHandler с disposition 'other', такие окна Electron
 * не создаёт, и requestWindow отвечает «no window». Проверено на сборке 38.8.6. Хуже того, неудачный
 * requestWindow съедает жест пользователя, и запасной путь в том же нажатии уже не срабатывает, —
 * поэтому в приложении сразу идём обычным PiP видеоэлемента: он там работает без правок оболочки.
 *
 * Видеоэлемент окна живёт вне React и не привязан к комнате канала: смотреть трансляцию поверх
 * других окон и переписываться в текстовом канале — ровно то, ради чего окно выносят. Дорожку
 * к нему цепляем через track.attach: LiveKit видит элемент в окне поверх и не останавливает поток,
 * даже когда основное окно свёрнуто (adaptiveStream).
 */
import { useSyncExternalStore } from 'react'
import type { Track } from 'livekit-client'
import { isDesktop } from '@/lib/desktop'
import { useVoiceStore } from '@/store/voice'

interface DocumentPip {
  window: Window | null
  requestWindow: (options?: { width?: number; height?: number }) => Promise<Window>
}

const documentPip: DocumentPip | null = !isDesktop && typeof window !== 'undefined'
  ? ((window as Window & { documentPictureInPicture?: DocumentPip }).documentPictureInPicture ?? null)
  : null
const videoPip = typeof document !== 'undefined' && document.pictureInPictureEnabled === true

/** Есть ли где открыть окно поверх: в Firefox и старых браузерах кнопки нет. */
export const canPopOut = documentPip !== null || videoPip

/*
 * API есть, а окна нет — так ведут себя и другие обёртки на Electron, не только наша (например,
 * встроенный браузер приложения Claude). После первой неудачи идём обычным PiP видеоэлемента;
 * само первое нажатие уже потрачено: неудачный requestWindow съедает жест.
 */
let documentPipBroken = false

/** Что в окне: экран человека или его камера. Окно одно на всех — новое закрывает прежнее. */
export type PopKind = 'screen' | 'camera'

interface PopOut {
  kind: PopKind
  userId: string
  track: Track
  video: HTMLVideoElement
  close: () => void
}

let current: PopOut | null = null
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

const snapshot = () => (current ? `${current.kind}:${current.userId}` : null)

/** Чья трансляция (или камера — kind) сейчас в отдельном окне. */
export function usePoppedOut(kind: PopKind = 'screen'): string | null {
  const key = useSyncExternalStore(subscribe, snapshot, () => null)
  return key?.startsWith(`${kind}:`) ? key.slice(kind.length + 1) : null
}

export function closePopOut(): void {
  current?.close()
}

function streamVideo(doc: Document): HTMLVideoElement {
  const video = doc.createElement('video')
  video.muted = true
  video.autoplay = true
  video.playsInline = true
  return video
}

/** Окно по пропорциям картинки: вертикальный монитор не должен лечь полосой в широкое окно. */
function windowSize(aspect: number): { width: number; height: number } {
  const a = aspect > 0.2 && aspect < 5 ? aspect : 16 / 9
  return a >= 1 ? { width: 480, height: Math.round(480 / a) } : { width: Math.round(360 * a), height: 360 }
}

/**
 * Вынести трансляцию или камеру (kind) в окно поверх. Вызывать прямо из нажатия: оба пути требуют жеста.
 * aspect — пропорции картинки на странице (videoWidth / videoHeight), если уже известны.
 * 'retry' — Document PiP не дал окна, следующее нажатие пойдёт запасным путём.
 */
export async function popOutStream(userId: string, track: Track, title: string, aspect = 16 / 9, kind: PopKind = 'screen'): Promise<'ok' | 'retry'> {
  closePopOut()
  if (documentPip && !documentPipBroken) {
    try {
      await openDocument(kind, userId, track, title, aspect)
    } catch (err) {
      if (!videoPip) throw err
      documentPipBroken = true
      if (err instanceof Error) console.warn('Document PiP не дал окна, дальше — PiP видео:', err.message)
      return 'retry'
    }
    return 'ok'
  }
  if (!videoPip) throw new Error('окно поверх не поддерживается')
  await openVideo(kind, userId, track)
  return 'ok'
}

async function openDocument(kind: PopKind, userId: string, track: Track, title: string, aspect: number): Promise<void> {
  const win = await documentPip!.requestWindow(windowSize(aspect))
  const doc = win.document
  doc.title = title
  const style = doc.createElement('style')
  style.textContent = 'html,body{margin:0;height:100%;background:#000;overflow:hidden}video{display:block;width:100%;height:100%;object-fit:contain}'
  doc.head.append(style)
  const video = streamVideo(doc)
  // сначала в окно, потом attach: так LiveKit сразу видит элемент в окне поверх
  doc.body.append(video)
  const entry: PopOut = { kind, userId, track, video, close: () => win.close() }
  track.attach(video)
  current = entry
  win.addEventListener('pagehide', () => {
    entry.track.detach(video)
    if (current === entry) {
      current = null
      emit()
    }
  }, { once: true })
  emit()
}

async function openVideo(kind: PopKind, userId: string, track: Track): Promise<void> {
  const video = streamVideo(document)
  // на странице элемент невидим: картинку показывает системное окно поверх
  video.style.cssText = 'position:fixed;left:0;top:0;width:2px;height:2px;opacity:0;pointer-events:none'
  document.body.append(video)
  const entry: PopOut = { kind, userId, track, video, close: () => void document.exitPictureInPicture().catch(() => undefined) }
  const drop = () => {
    entry.track.detach(video)
    video.remove()
    if (current === entry) {
      current = null
      emit()
    }
  }
  track.attach(video)
  try {
    // PiP просит видео с известным размером; кадр с живой дорожки приходит за доли секунды
    if (video.readyState < HTMLMediaElement.HAVE_METADATA) {
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('нет кадров')), 3000)
        video.addEventListener('loadedmetadata', () => {
          clearTimeout(timer)
          resolve()
        }, { once: true })
      })
    }
    await video.requestPictureInPicture()
  } catch (err) {
    drop()
    throw err
  }
  video.addEventListener('leavepictureinpicture', drop, { once: true })
  current = entry
  emit()
}

/*
 * Окно следует за дорожкой: закрыли просмотр, трансляция или камера выключились, мы вышли из канала —
 * окно закрывается; дорожка сменилась (переподключение) — цепляем новую к тому же элементу.
 */
useVoiceStore.subscribe((s) => {
  const entry = current
  if (!entry) return
  const track = entry.kind === 'camera' ? s.cameras[entry.userId] : s.screens[entry.userId]
  if (track === entry.track) return
  if (!track) {
    entry.close()
    return
  }
  entry.track.detach(entry.video)
  entry.track = track
  track.attach(entry.video)
})
